import { updateFileWithPageNumber } from "@/state/transactions/CurrentTransactionEvents";
import { Member } from "@/type/core/offices/offices.types";
import ConsentModal from "@components/core/pdf/ConsentModal";
import PdfContainerLeftPanel from "@components/core/pdf/PdfContainerLeftPanel";
import PdfContainerRightPanel from "@components/core/pdf/PdfContainerRightPanel";
import {
  File,
  InitialFieldsAlignment,
  Signatory,
  Signature,
} from "@type/core/dashboard/dashboard.types";
import { TransactionType } from "@type/core/transaction/transaction.types";
import {
  DEFAULT_INITIAL_FIELDS_ALIGNMENT,
  DEFAULT_INITIAL_FIELDS_Y_POSITION,
} from "@utils/Constant";
import { Row } from "antd";
import {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
} from "react";

interface PdfContainerProps {
  index: number;
  file: File;
  numberOfFiles: number;
  transactionType: TransactionType;
  signatories: Signatory[];
  signatures: Signature[];
  showSeparator?: boolean;
  manualPositioning: boolean;
  setManualPositioning: Dispatch<SetStateAction<boolean>>;
  buttonLoading?: boolean;
  setButtonLoading?: Dispatch<SetStateAction<boolean>>;
  className?: string;
  members: Member[];
  officeId: string;
  displaySelectSignaturesForAll?: boolean;
}

const PdfContainer: FunctionComponent<PdfContainerProps> = ({
  index,
  file,
  numberOfFiles,
  transactionType,
  signatories,
  signatures,
  showSeparator = false,
  setManualPositioning,
  buttonLoading,
  className = "",
  members,
  officeId,
  // displaySelectSignaturesForAll = false,
}) => {
  const initialFieldsFromFile = useMemo(() => file.initialFields, [file]);
  const src = useMemo(() => file.url, [file]);

  const [loading, setLoading] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isInitialFieldsActivated, setIsInitialFieldsActivated] =
    useState<boolean>(!!initialFieldsFromFile);
  const [initialFieldsY, setInitialFieldsY] = useState<number>(
    initialFieldsFromFile?.y ?? DEFAULT_INITIAL_FIELDS_Y_POSITION,
  );
  const [initialFieldsAlignment, setInitialFieldsAlignment] =
    useState<InitialFieldsAlignment>(
      initialFieldsFromFile?.alignment ?? DEFAULT_INITIAL_FIELDS_ALIGNMENT,
    );
  useEffect(() => {
    setIsInitialFieldsActivated(!!initialFieldsFromFile);
    setInitialFieldsY(
      initialFieldsFromFile?.y ?? DEFAULT_INITIAL_FIELDS_Y_POSITION,
    );
    setInitialFieldsAlignment(
      initialFieldsFromFile?.alignment ?? DEFAULT_INITIAL_FIELDS_ALIGNMENT,
    );
  }, [initialFieldsFromFile]);

  const setTotalPageNumber = (totalPageNumber: number) => {
    file.pages = totalPageNumber;
    updateFileWithPageNumber(file);
  };

  return (
    <div
      className={`app-pdf-container ${className}`}
      data-test="section"
      data-alt={file.name}
    >
      {showSeparator && <hr />}
      <h2 title={file.name} className="overflow-ellipsis">
        {file.name}
      </h2>
      <Row gutter={15} className="pdf-container" id={"document_" + index}>
        <PdfContainerLeftPanel
          src={src}
          index={index}
          setLoading={setLoading}
          setPageNumber={setPageNumber}
          isInitialFieldsActivated={isInitialFieldsActivated}
          initialFieldsY={initialFieldsY}
          initialFieldsAlignment={initialFieldsAlignment}
          pageNumber={pageNumber}
          filename={file.name}
          transactionType={transactionType}
          getTotalPageNumber={setTotalPageNumber}
          setInitialFieldsY={setInitialFieldsY}
        />
        <PdfContainerRightPanel
          src={src}
          loading={loading}
          index={index}
          signatories={signatories}
          signatures={signatures}
          buttonLoading={buttonLoading}
          transactionType={transactionType}
          members={members}
          setManualPositioning={setManualPositioning}
          isInitialFieldsActivated={isInitialFieldsActivated}
          initialFieldsY={initialFieldsY}
          initialFieldsAlignment={initialFieldsAlignment}
          filename={file.name}
          officeId={officeId}
          pageNumber={pageNumber}
          numberOfFiles={numberOfFiles}
          setIsInitialFieldsActivated={setIsInitialFieldsActivated}
        />
      </Row>
      {index == 1 && <ConsentModal signatures={signatures} />}
    </div>
  );
};

export default PdfContainer;
